import type { ReactElement, ReactNode } from 'react';
import { dataAsOf, formatDate, type ReportDetail } from '@tallyframe/core';
import { SAVE_STATE_LABEL, type SaveState } from '../editing/useReportEditing';
import { Badge, type BadgeTone } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';
import { SegmentedControl } from '../ui/SegmentedControl';
import { useIsPhone } from '../ui/useIsPhone';

export interface EditorToolbarProps {
  mode: 'edit' | 'preview';
  onModeChange(mode: 'edit' | 'preview'): void;
  panel: 'inspector' | 'history';
  onPanelChange(panel: 'inspector' | 'history'): void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo(): void;
  onRedo(): void;
  /** A version preview is showing: undo and redo act on nothing the person can see. */
  historyLocked: boolean;
  freshness: ReportDetail['freshness'];
  saveState: SaveState;
  /** The report was added from a bundled sample. */
  sample?: boolean;
  /** The Share control. */
  share: ReactNode;
  /** The Export menu. */
  exportMenu: ReactNode;
  /** Milestone 5 renders renderer filter components here; absent today, so nothing renders. */
  filters?: ReactNode;
}

const MODE_OPTIONS = [
  { value: 'edit', label: 'Edit' },
  { value: 'preview', label: 'Preview' }
] as const;

const PANEL_OPTIONS = [
  { value: 'inspector', label: 'Inspector' },
  { value: 'history', label: 'History' }
] as const;

const SAVE_TONE: Record<SaveState, BadgeTone> = {
  saved: 'success',
  saving: 'info',
  unsaved: 'warn',
  conflict: 'danger',
  error: 'danger'
};

/** The same date the Data panel shows: core's data-as-of for the active snapshot. */
function freshnessText(freshness: ReportDetail['freshness']): string {
  if (!freshness.receivedAt) return 'No data yet';
  const asOf = dataAsOf({
    receivedAt: freshness.receivedAt,
    sourceObservedAt: freshness.sourceObservedAt ?? undefined
  });
  return `Data as of ${formatDate(asOf)}`;
}

/**
 * The report editor's toolbar: mode, undo/redo, the side-panel switch, then
 * freshness and save state (visible in every mode), Share and Export.
 */
export function EditorToolbar(props: EditorToolbarProps): ReactElement {
  const undoRedoLocked = props.historyLocked;
  // Phones view and share: freshness, then Share and Export. Mode, undo/redo and
  // the panel switch belong to editing, which phones don't offer.
  if (useIsPhone()) {
    return (
      <div data-testid="editor-toolbar" className="flex items-center gap-2 w-full">
        <span data-testid="freshness" className="min-w-0 truncate">
          <Badge tone="neutral">{freshnessText(props.freshness)}</Badge>
        </span>
        <div data-slot="spacer" className="grow" />
        {props.share}
        {props.exportMenu}
      </div>
    );
  }
  return (
    <div data-testid="editor-toolbar" className="flex flex-wrap items-center gap-3 w-full">
      <SegmentedControl
        label="Report mode"
        value={props.mode}
        onChange={(value) => props.onModeChange(value as 'edit' | 'preview')}
        options={MODE_OPTIONS}
      />
      <IconButton label="Undo" isDisabled={!props.canUndo || undoRedoLocked} onPress={props.onUndo}>
        <Icon name="undo" size={16} />
      </IconButton>
      <IconButton label="Redo" isDisabled={!props.canRedo || undoRedoLocked} onPress={props.onRedo}>
        <Icon name="redo" size={16} />
      </IconButton>
      <SegmentedControl
        label="Side panel"
        value={props.panel}
        onChange={(value) => props.onPanelChange(value as 'inspector' | 'history')}
        options={PANEL_OPTIONS}
      />
      {props.filters}
      {props.sample ? <Badge tone="neutral">Sample</Badge> : null}
      <div data-slot="spacer" className="grow" />
      <span data-testid="freshness">
        <Badge tone="neutral">{freshnessText(props.freshness)}</Badge>
      </span>
      <span data-testid="save-status" data-state={props.saveState} role="status" aria-live="polite">
        <Badge tone={SAVE_TONE[props.saveState]}>{SAVE_STATE_LABEL[props.saveState]}</Badge>
      </span>
      {props.share}
      {props.exportMenu}
    </div>
  );
}
